<script setup lang="ts">
/**
 * Paiement en plusieurs fois (adhésion, billetterie, boutique — Karim
 * 2026-10-21, proposition « A » retenue) : un interrupteur « Payer en
 * plusieurs fois », éteint par défaut (= en une fois), dans la même carte que
 * « Faire un don ». Allumé : la liste des possibilités comme des moyens de
 * paiement (« En 3 fois — 25,63 € aujourd'hui, puis 20,56 €/mois », montant
 * par mois à droite), la plus courte cochée d'office.
 *
 * N'apparaît que si l'association l'a activé ET que le panier le permet
 * (règles partagées avec le serveur : au moins 10 € par part, dernier
 * paiement avant `dernierJour`). Montants au centime de ce que le serveur
 * prélèvera.
 */
import { echeancierDuPanier, jourCivilParis, nombresPartsProposables } from "@donoor/utils";
import { suiteIdentique, type PartDuPanier } from "~/utils/plusieursFois";

const props = defineProps<{
  /** Réglage de l'association (`installments_counts`). */
  reglage: unknown;
  /**
   * Dernier jour où un paiement peut tomber (`null` = aucune limite) : fin de
   * l'adhésion, événement + 50 jours, achat + 50 jours —
   * `dernierJourPlusieursFois*` de @donoor/utils, comme le serveur.
   */
  dernierJour: string | null;
  /** Euros divisés (tarif + options, après code promo). */
  montantDivise: number;
  /** Euros payés le 1er jour pour l'association (don, frais de port). */
  enPlus?: number;
  /** Soutien à Donoor (euros), payé le 1er jour. */
  soutien?: number;
  /** Commission Donoor en DÉCIMAL (0,0395). */
  tauxCommission: number;
  fraisCouverts: boolean;
  /** Montant débité en une fois (gardé pour compatibilité des appelants). */
  totalUneFois?: number;
  /** Le paiement en plusieurs fois n'est pas possible ici (PayPal seul, demande…). */
  indisponible?: boolean;
  /** Nombre de parts choisi (0 = en une fois). */
  modelValue: number;
}>();

const emit = defineEmits<{ (e: "update:modelValue", v: number): void }>();

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();
const tr = (cle: string, params?: Record<string, unknown>) => t(`plusieursFois.choix.${cle}`, params ?? {});

const possibles = computed(() =>
  props.indisponible
    ? []
    : nombresPartsProposables(props.reglage, Math.round((Number(props.montantDivise) || 0) * 100), {
        dernierJour: props.dernierJour,
        premierJour: jourCivilParis(),
      }),
);

// Un choix qui n'est plus possible (code promo, panier modifié) repasse en 1 fois.
watch(
  possibles,
  (liste) => {
    if (props.modelValue && !liste.includes(props.modelValue)) emit("update:modelValue", 0);
  },
  { immediate: true },
);


interface Option {
  n: number;
  sousTitre: string;
  /** Montant de chaque mois (à droite de la tuile). */
  parMois: number;
  parts: PartDuPanier[];
}

const options = computed<Option[]>(() =>
  possibles.value.map((n) => {
    const parts = echeancierDuPanier({
      montantDiviseEuros: props.montantDivise,
      nombre: n,
      premierJour: jourCivilParis(),
      enPlusEuros: props.enPlus ?? 0,
      soutienEuros: props.soutien ?? 0,
      tauxCommission: props.tauxCommission,
      fraisCouverts: props.fraisCouverts,
    });
    const suite = suiteIdentique(parts);
    const premiere = parts[0]?.montant ?? 0;
    const sousTitre =
      suite && Math.abs(suite.montant - premiere) < 0.005
        ? tr("sousMois", { n })
        : suite
          ? tr("sousDifferent", { premier: formatCurrency(premiere), n: suite.nombre, montant: formatCurrency(suite.montant) })
          : tr("sousDivers", { n, premier: formatCurrency(premiere) });
    return { n, sousTitre, parMois: suite?.montant ?? premiere, parts };
  }),
);

/** Calendrier de l'icône : mois court et jour d'aujourd'hui, dans la langue de la page. */
const { locale } = useI18n();
const maintenant = new Date();
const moisCourt = computed(() =>
  new Intl.DateTimeFormat(locale.value, { month: "short", timeZone: "Europe/Paris" }).format(maintenant).replace(".", ""),
);
const jourDuMois = computed(() =>
  new Intl.DateTimeFormat(locale.value, { day: "numeric", timeZone: "Europe/Paris" }).format(maintenant),
);

/** Interrupteur : allumé = la plus courte possibilité, éteint = en une fois. */
const active = computed({
  get: () => props.modelValue >= 2,
  set: (v: boolean) => emit("update:modelValue", v ? (possibles.value[0] ?? 0) : 0),
});
</script>

<template>
  <div
    v-if="possibles.length"
    class="mt-3 rounded-2xl border border-gray-200 dark:border-[#262626] overflow-hidden"
    data-testid="choix-plusieurs-fois"
  >
    <!-- Toute la ligne allume / éteint (pas seulement l'interrupteur). -->
    <div
      class="flex items-center justify-between gap-3 p-4 cursor-pointer select-none"
      role="button"
      tabindex="0"
      :aria-pressed="active"
      @click="active = !active"
      @keydown.enter.prevent="active = !active"
      @keydown.space.prevent="active = !active"
    >
      <div class="flex items-center gap-2.5">
        <!-- Calendrier façon icône Calendrier d'Apple : le mois en haut, le
             jour d'aujourd'hui en grand. Pâle éteint, net allumé. -->
        <span
          class="size-8 shrink-0 rounded-[9px] bg-white dark:bg-[#1c1c1e] flex flex-col items-center overflow-hidden shadow-[0_0_0_1px_rgba(0,0,0,0.1),0_1px_2px_rgba(0,0,0,0.08)] transition-opacity duration-300"
          :class="active ? 'opacity-100' : 'opacity-50'"
          aria-hidden="true"
        >
          <span class="w-full bg-[#1d1d1f] dark:bg-white text-white dark:text-[#1d1d1f] text-center text-[7px] font-bold uppercase leading-[11px] tracking-wide">{{ moisCourt }}</span>
          <span class="flex-1 flex items-center text-[14px] font-semibold leading-none text-[#1d1d1f] dark:text-white tabular-nums">{{ jourDuMois }}</span>
        </span>
        <span class="text-sm font-medium text-[#2d2e5c] dark:text-gray-300">{{ tr('titre') }}</span>
      </div>
      <USwitch
        v-model="active"
        @click.stop
        size="xs"
        data-testid="switch-plusieurs-fois"
        :ui="{ base: 'data-[state=checked]:bg-[#1d1d1f]! dark:data-[state=checked]:bg-white!' }"
      />
    </div>

    <!-- Ouverture / fermeture en douceur : hauteur animée (grille 0fr → 1fr)
         et fondu, la liste reste montée. -->
    <div
      class="grid transition-[grid-template-rows,opacity] duration-300 ease-out"
      :class="active ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'"
      :aria-hidden="!active"
      :inert="!active"
    >
    <div class="min-h-0 overflow-hidden">
    <div
      class="border-t border-gray-200 dark:border-[#262626] divide-y divide-gray-100 dark:divide-[#1e1e1e]"
      role="radiogroup"
      :aria-label="tr('titre')"
    >
      <button
        v-for="o in options"
        :key="o.n"
        type="button"
        role="radio"
        :aria-checked="modelValue === o.n"
        :data-testid="`choix-plusieurs-fois-${o.n}`"
        class="w-full flex items-center gap-3 px-4 py-3 text-left transition-[background-color,opacity] duration-200"
        :class="modelValue === o.n
          ? 'bg-[#f2f2f7] dark:bg-[#1c1c1e]'
          : 'opacity-55 hover:opacity-100'"
        @click="emit('update:modelValue', o.n)"
      >
        <!-- « 3× » en typographie : gros chiffre, « × » plus petit. Noir quand
             choisi, gris clair sinon. Largeur fixe : les titres restent alignés. -->
        <span
          class="w-11 shrink-0 flex items-baseline justify-center leading-none transition-colors"
          :class="modelValue === o.n ? 'text-[#1d1d1f] dark:text-white' : 'text-[#c7c7cc] dark:text-[#48484a]'"
        >
          <span class="text-[28px] font-extrabold tracking-[-0.04em] tabular-nums">{{ o.n }}</span>
          <span class="ml-[1px] text-[17px] font-bold">×</span>
        </span>
        <span class="min-w-0 flex-1">
          <span class="block text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ tr('enNFois', { n: o.n }) }}</span>
          <span class="block text-xs text-[#717198] dark:text-gray-500 mt-0.5">{{ o.sousTitre }}</span>
        </span>
        <span class="shrink-0 text-sm font-semibold tabular-nums text-[#2d2e5c] dark:text-white">
          {{ formatCurrency(o.parMois) }}<span class="text-xs font-normal text-[#717198] dark:text-gray-500">{{ tr('parMoisCourt') }}</span>
        </span>
      </button>
    </div>
    </div>
    </div>
  </div>
</template>
